import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LabelList } from "recharts";
import { listCreatives, getCreative, getEvents, downloadEventsCsv, isAuthed } from "../lib/api";
import "../studio.css";

const AMBER = "#3b82f6";
const AMBER2 = "#2563eb";

function AnimatedBars({ data, title, testid, color = AMBER }) {
  const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]).map(([name, count]) => ({ name, count }));
  return (
    <div className="panel" data-testid={testid}>
      <h3>{title}</h3>
      {entries.length === 0 ? <div className="hint">No data yet.</div> : (
        <ResponsiveContainer width="100%" height={Math.max(120, entries.length * 46)}>
          <BarChart data={entries} layout="vertical" margin={{ top: 4, right: 34, left: 4, bottom: 4 }}>
            <XAxis type="number" hide />
            <YAxis type="category" dataKey="name" width={120} tick={{ fill: "var(--text-dim)", fontSize: 11, fontFamily: "var(--font-mono)" }} axisLine={false} tickLine={false} />
            <Tooltip cursor={{ fill: "rgba(245,165,36,.08)" }} contentStyle={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12 }} />
            <Bar dataKey="count" radius={[0, 6, 6, 0]} animationDuration={900} animationEasing="ease-out" isAnimationActive>
              <LabelList dataKey="count" position="right" fill="var(--text)" fontSize={12} fontWeight={700} />
              {entries.map((e, idx) => <Cell key={idx} fill={idx % 2 ? AMBER2 : color} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

function WheelFunnel({ byEv, testid }) {
  const steps = [
    ["spin_start", "Spins started"],
    ["spin_complete", "Spins completed"],
    ["win", "Wins"],
    ["reward_revealed", "Rewards revealed"],
    ["clickThru", "Clickthroughs"],
  ];
  const rows = steps.map(([k, label]) => ({ k, label, count: (byEv && byEv[k]) || 0 }));
  const top = rows[0].count || 0;
  return (
    <div className="panel" data-testid={testid}>
      <h3>Spin → reward funnel</h3>
      {top === 0 ? <div className="hint">No spins captured yet.</div> : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {rows.map((r, i) => {
            const pct = top ? Math.round((r.count / top) * 100) : 0;
            const conv = i > 0 && rows[i - 1].count ? Math.round((r.count / rows[i - 1].count) * 100) : null;
            return (
              <div key={r.k} data-testid={`funnel-${r.k}`}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 3 }}>
                  <span className="eyebrow">{r.label}</span>
                  <span style={{ fontFamily: "var(--font-mono)", fontWeight: 700 }}>
                    {r.count}{conv != null && <span style={{ color: "var(--text-dim)", fontWeight: 500 }}> · {conv}%</span>}
                  </span>
                </div>
                <div style={{ height: 22, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 6, overflow: "hidden" }}>
                  <div style={{ width: `${pct}%`, height: "100%", background: `linear-gradient(90deg, ${AMBER}, ${AMBER2})`, borderRadius: 6, transition: "width .6s ease-out" }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PlayerSupport({ rows, testid }) {
  return (
    <div className="panel" data-testid={testid}>
      <h3>Player resize support <span style={{ fontSize: 12, color: "var(--text-dim)", fontWeight: 500 }}>— video_resize outcome by player WebView</span></h3>
      <div className="tbl-scroll">
        <table className="evt">
          <thead><tr><th>Player / WebView</th><th>Resolved</th><th>Rejected</th><th>Baked</th><th>Support rate</th></tr></thead>
          <tbody data-testid="player-support-body">
            {rows.map((r, i) => {
              const col = r.okRate >= 90 ? "#16a34a" : r.okRate >= 50 ? "#d97706" : "#dc2626";
              return (
                <tr key={r.ua} data-testid={`player-row-${i}`} title={r.sampleUa || undefined}>
                  <td>{r.ua}</td>
                  <td style={{ fontWeight: 700, color: "#16a34a" }}>{r.resolved}</td>
                  <td style={{ fontWeight: 700, color: r.rejected ? "#dc2626" : "inherit" }}>{r.rejected}</td>
                  <td>{r.baked}</td>
                  <td><span style={{ fontFamily: "var(--font-mono)", fontWeight: 800, color: col }}>{r.okRate}%</span>
                    <span style={{ fontSize: 11, color: "var(--text-dim)" }}> of {r.total}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="hint" style={{ marginTop: 8 }}>Resolved = player honored the video reframe · Baked = pre-squeezed video served (layout guaranteed) · Rejected = overlay fell back to full-screen. Verify every target player before go-live. Hover a row for the raw user-agent.</div>
    </div>
  );
}

function Analytics() {
  const nav = useNavigate();
  const [creatives, setCreatives] = useState([]);
  const [cid, setCid] = useState("");
  const [data, setData] = useState(null);
  const [labelMap, setLabelMap] = useState({ vote: {}, frame: {} });
  const [range, setRange] = useState("today");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  const computeRange = () => {
    const now = new Date();
    if (range === "today") { const d = new Date(now); d.setHours(0, 0, 0, 0); return { from: d.toISOString() }; }
    if (range === "7d") return { from: new Date(now - 7 * 864e5).toISOString() };
    if (range === "30d") return { from: new Date(now - 30 * 864e5).toISOString() };
    if (range === "custom") {
      const r = {};
      if (customFrom) r.from = new Date(customFrom + "T00:00:00").toISOString();
      if (customTo) r.to = new Date(customTo + "T23:59:59").toISOString();
      return r;
    }
    return {};
  };

  useEffect(() => { if (!isAuthed()) nav("/login"); }, [nav]);
  useEffect(() => { listCreatives().then(setCreatives).catch(() => {}); }, []);
  useEffect(() => {
    if (!cid) { setLabelMap({ vote: {}, frame: {} }); return; }
    getCreative(cid).then((c) => {
      const cfg = c.config || {};
      const vote = {}, frame = {};
      (cfg.options || []).forEach((o, k) => { vote[k] = o.label || `Option ${k}`; });
      (cfg.frames || []).forEach((f, k) => { frame[k] = f.label || `Frame ${k}`; });
      setLabelMap({ vote, frame });
    }).catch(() => {});
  }, [cid]);
  const load = () => getEvents(cid || undefined, computeRange()).then(setData).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); /* eslint-disable-next-line */ }, [cid, range, customFrom, customTo]);

  const mapVal = (ev, val) => {
    if (ev === "vote") return labelMap.vote[val] || `Option ${val}`;
    if (ev === "frame_view" || ev === "cta_click") return labelMap.frame[val] || `Frame ${val}`;
    return String(val);
  };
  const byValRows = data && data.byVal ? Object.entries(data.byVal) : [];
  const be = data?.byEv || {};
  const isWheel = !!(be.spin_start || be.spin_complete || be.win || be.lose);
  const prizeDist = (data?.byVal && data.byVal.win) || {};
  const psRows = data?.playerSupport || [];

  return (
    <div className="app">
        <header className="bar">
          <h1 className="page-title" data-testid="page-title">Analytics</h1>
          <div className="spacer" />
          <button className="btn primary" onClick={() => downloadEventsCsv(cid || undefined, computeRange())} data-testid="export-csv">Export CSV</button>
        </header>
        <div className="an-wrap">
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10, flexWrap: "wrap" }}>
            <span className="eyebrow">Creative</span>
            <select className="select-cr" value={cid} onChange={(e) => setCid(e.target.value)} data-testid="analytics-select">
              <option value="">All creatives</option>
              {creatives.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.id})</option>)}
            </select>
            <span className="eyebrow" style={{ marginLeft: 8 }}>Range</span>
            <div className="seg" style={{ width: "auto" }} data-testid="range-seg">
              {[["today", "Today"], ["7d", "7 days"], ["30d", "30 days"], ["all", "All"], ["custom", "Custom"]].map(([id, lbl]) => (
                <button key={id} type="button" aria-pressed={range === id} data-testid={`range-${id}`}
                  onClick={() => setRange(id)} style={{ padding: "6px 12px" }}>{lbl}</button>
              ))}
            </div>
            {range === "custom" && (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input type="date" className="select-cr" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} data-testid="range-from" />
                <span className="hint">to</span>
                <input type="date" className="select-cr" value={customTo} onChange={(e) => setCustomTo(e.target.value)} data-testid="range-to" />
              </div>
            )}
          </div>

          <div className="an-grid">
            <div className="stat" data-testid="stat-impressions"><div className="k">Impressions</div><div className="v">{data?.impressions ?? 0}</div></div>
            <div className="stat" data-testid="stat-interactions"><div className="k">Interactions</div><div className="v">{data?.interactions ?? 0}</div></div>
            <div className="stat" data-testid="stat-rate"><div className="k">Interaction rate</div><div className="v">{data?.interactionRate ?? 0}%</div></div>
            <div className="stat" data-testid="stat-scans"><div className="k">QR scans</div><div className="v">{data?.scans ?? 0}<span style={{ fontSize: 14, color: "var(--text-dim)", fontWeight: 600 }}> · {data?.scanRate ?? 0}%</span></div></div>
            <div className="stat" data-testid="stat-total"><div className="k">Total events</div><div className="v">{data?.total ?? 0}</div></div>
          </div>

          <div className="an-cols" style={{ marginBottom: 14 }}>
            <AnimatedBars data={data?.byEv} title="Events by type" testid="by-ev" />
            <div className="panel" data-testid="by-val">
              <h3>Vote splits &amp; frame views</h3>
              {byValRows.length === 0 && <div className="hint">No granular values yet (votes / frame views appear here). {cid ? "" : "Tip: pick a creative to see option & frame labels."}</div>}
              {byValRows.map(([ev, vals]) => {
                const mapped = {};
                Object.entries(vals).forEach(([val, cnt]) => { mapped[mapVal(ev, val)] = cnt; });
                return (
                  <div key={ev} style={{ marginBottom: 8 }}>
                    <div className="eyebrow" style={{ marginBottom: 4 }}>{ev}</div>
                    <ResponsiveContainer width="100%" height={Math.max(90, Object.keys(mapped).length * 44)}>
                      <BarChart data={Object.entries(mapped).sort((a, b) => b[1] - a[1]).map(([name, count]) => ({ name, count }))}
                        layout="vertical" margin={{ top: 2, right: 34, left: 4, bottom: 2 }}>
                        <XAxis type="number" hide />
                        <YAxis type="category" dataKey="name" width={130} tick={{ fill: "var(--text-dim)", fontSize: 11 }} axisLine={false} tickLine={false} />
                        <Tooltip cursor={{ fill: "rgba(245,165,36,.08)" }} contentStyle={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12 }} />
                        <Bar dataKey="count" radius={[0, 6, 6, 0]} animationDuration={900} isAnimationActive>
                          <LabelList dataKey="count" position="right" fill="var(--text)" fontSize={12} fontWeight={700} />
                          {Object.keys(mapped).map((_, idx) => <Cell key={idx} fill={idx % 2 ? AMBER2 : AMBER} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                );
              })}
            </div>
          </div>

          {isWheel && (
            <div className="an-cols" style={{ marginBottom: 14 }} data-testid="wheel-analytics">
              <WheelFunnel byEv={be} testid="wheel-funnel" />
              <AnimatedBars data={prizeDist} title="Prize distribution (wins)" testid="wheel-prize-dist" />
            </div>
          )}

          {psRows.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <PlayerSupport rows={psRows} testid="player-support" />
            </div>
          )}

          <div className="panel">
            <h3>Recent events</h3>
            <div className="tbl-scroll">
              <table className="evt">
                <thead><tr><th>Time</th><th>Creative</th><th>v</th><th>Event</th><th>Val</th><th>Tid</th><th>IP</th></tr></thead>
                <tbody data-testid="events-table-body">
                  {(data?.recent || []).map((r) => (
                    <tr key={r.id}>
                      <td>{new Date(r.receivedAt).toLocaleTimeString()}</td>
                      <td>{r.creativeId || "-"}</td><td>{r.version ?? "-"}</td>
                      <td>{r.ev}</td><td>{r.val ?? "-"}</td><td>{r.tid || "-"}</td><td>{r.ip || "-"}</td>
                    </tr>
                  ))}
                  {(!data || data.recent.length === 0) && <tr><td colSpan={7} className="hint">No events captured yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
  );
}

export default Analytics;
